// desyr as an installed app (32-install.md): the browser's install offer kept from the page's start,
// the "App" card of About, the sign-in page's Install button with its dialog (also opened by
// "?install"), the install guide on iPhone and iPad, and the installed app's own title bar.
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Button } from '../blocks/Button';
import { Quiet } from '../blocks/Quiet';
import { Dialog, DialogButtons, useTopModal } from '../blocks/Dialog';
import { Icon, icons } from '../blocks/icons';
import { PRODUCT } from '../core/product';
import { useScreen } from '../blocks/Page';
import { t, type Key } from '../i18n';
import { readJson, writeJson } from '../core/storage';
import { readSession } from '../core/session';
import { useDialogsAhead } from '../security/firstDialogs';
import { navigate } from '../core/router';
import logo from '../assets/logo.svg';
import download from '@material-symbols/svg-400/outlined/download.svg?raw';
import share from '@material-symbols/svg-400/outlined/ios_share.svg?raw';
import arrowDown from '@material-symbols/svg-400/outlined/arrow_downward.svg?raw';
import arrowUpRight from '@material-symbols/svg-400/outlined/north_east.svg?raw';
import './install.css';

export type InstallState = 'installed' | 'available' | 'ios' | 'safari' | 'menu' | 'unsupported';
type Offer = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: string }> };

let offer: Offer | undefined;
let installedNow = false;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

/** "?install" was in the address at the start (32, By address); taken out of it at once. */
let byAddress = false;
/** "?install" signed in on an iPad: Settings opens at About (32, v1.58.34). */
let aboutAsked = false;
export const takeAboutAsked = () => [aboutAsked, (aboutAsked = false)][0];

/** Called at the very start: the browser's offer is kept (its own small bar never shows); "?install" is noted. */
export function catchInstallOffer(): void {
  addEventListener('beforeinstallprompt', (e) => (e.preventDefault(), (offer = e as Offer), changed()));
  addEventListener('appinstalled', () => ((installedNow = true), (offer = undefined), changed()));
  const query = new URLSearchParams(location.search);
  if (!query.has('install')) return;
  byAddress = true;
  query.delete('install');
  history.replaceState(history.state, '', location.pathname + (query.size ? `?${query}` : '') + location.hash);
}

export const ios = () => /iPhone|iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
/** Safari on an iPhone or iPad (not Chrome, Firefox or Edge there): where the install guide shows. */
export const iosSafari = () => ios() && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
export const isInstalled = () =>
  installedNow || ['standalone', 'window-controls-overlay', 'minimal-ui'].some((m) => matchMedia(`(display-mode: ${m})`).matches) || (navigator as { standalone?: boolean }).standalone === true;

export function installState(): InstallState {
  if (isInstalled()) return 'installed';
  if (offer) return 'available';
  if (ios()) return 'ios';
  const ua = navigator.userAgent;
  if (/Macintosh/.test(ua) && /Safari\//.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua)) return 'safari';
  if (/Chrome|Chromium|Edg/.test(ua)) return 'menu';
  return 'unsupported';
}
const useInstall = () => useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), installState);

/** Opens the browser's own install dialog; dismissed or gone, the menu way is shown. */
async function install(): Promise<void> {
  const it = offer;
  offer = undefined;
  if (!it) return changed();
  await it.prompt().catch(() => {});
  const { outcome } = await it.userChoice.catch(() => ({ outcome: 'dismissed' }));
  if (outcome === 'accepted') installedNow = true;
  changed();
}

const LINES: Record<InstallState, Key> = {
  installed: 'install.installedLine',
  available: 'install.availableLine',
  ios: 'install.iosLine',
  safari: 'install.safariLine',
  menu: 'install.menuLine',
  unsupported: 'install.unsupportedLine',
};

const Steps = ({ keys }: { keys: Key[] }) => (
  <ol className="install-steps">
    {keys.map((k, i) => (
      <li key={k}>
        {i === 0 && keys.length === 3 && k === 'install.iosStep1' ? (
          <>
            <Icon svg={share} size={16} /> {t(k)} <span className="install-quiet">{t('install.iosWhere')}</span>
          </>
        ) : (
          t(k, { product: PRODUCT })
        )}
      </li>
    ))}
  </ol>
);
const IOS_STEPS: Key[] = ['install.iosStep1', 'install.iosStep2', 'install.iosStep3'];

/** The title bar tip (32): a Chromium browser on a computer, static. */
function TitleBarTip({ state }: { state: InstallState }) {
  const computer = useScreen() === 'desktop';
  return computer && (state === 'available' || state === 'menu') ? <p className="install-quiet install-tip">{t('install.titleBarTip', { product: PRODUCT })}</p> : null;
}

/** Settings → About → "App". */
export function InstallCard() {
  const state = useInstall();
  return (
    <section className="settings-section">
      <h3>{t('install.app')}</h3>
      <div className="settings-card" data-desyr-install={state}>
        <div className="settings-card-row">
          <span className="settings-icon">
            <Icon svg={download} size={20} />
          </span>
          <div className="settings-card-text">
            <div className="settings-card-title">{t(state === 'installed' ? 'install.installed' : 'install.title')}</div>
            <div className="settings-card-line">{t(LINES[state])}</div>
            <TitleBarTip state={state} />
          </div>
          {state === 'installed' && <Icon svg={icons.check} size={20} />}
          {state === 'available' && (
            <Button small variant="filled" onClick={() => void install()}>
              {t('install.install')}
            </Button>
          )}
        </div>
        {state === 'ios' && <Steps keys={IOS_STEPS} />}
      </div>
    </section>
  );
}



const DISMISSED = 'desyr_install_dismissed';
const GUIDE_STEPS: Key[] = ['install.guideStep1', 'install.iosStep2', 'install.guideStep3'];
/** A sign-in in this page: the guide then shows once on Home (32, v1.58.30). */
let signedInHere = false;
export const guideOnHome = () => void (signedInHere = true);

/**
 * The install guide on iPhone and iPad (32, v1.58.30): in Safari, not installed, a card at the bottom
 * with the steps and an arrow to where Safari keeps Share; "Not now" keeps it away on this device.
 */
// What fails in the guide leaves it out, never the page around it: it never blocks sign-in (32).
export const InstallGuide = ({ where }: { where: 'sign-in' | 'home' | 'you' }) => (
  <Quiet>
    <Guide where={where} />
  </Quiet>
);

function Guide({ where }: { where: 'sign-in' | 'home' | 'you' }) {
  const state = useInstall();
  // In You always reachable, even after Not now (32, v1.58.31).
  // The sign-in page's also after Not now when the address asks for it (32, By address).
  const [shown, setShown] = useState(() => where === 'you' || (where === 'sign-in' && byAddress) || ((where === 'sign-in' || signedInHere) && !readJson(DISMISSED)));
  const on = shown && state === 'ios' && iosSafari();
  // One thing at a time (32, v1.58.33): Home's card waits while a dialog is open over Home, and after
  // a sign-in until its first dialogs are decided (at most 3 s); its "once" counts only once it has shown.
  const dialog = useTopModal(on && where === 'home');
  const ahead = useDialogsAhead(on && where === 'home' && signedInHere);
  const waiting = !!dialog || ahead;
  const visible = on && !waiting;
  useEffect(() => (visible && where === 'home' ? () => void (signedInHere = false) : undefined), [visible, where]);
  if (!visible) return null;
  const pad = !/iPhone/.test(navigator.userAgent);
  return (
    <aside className={`install-guide ${where}`} data-desyr-install-guide={where} aria-label={t('install.title')}>
      <img src={logo} alt="" width={40} height={40} />
      <div className="install-guide-text">
        <strong>{t('install.title')}</strong>
        <p>{t('install.guideLine')}</p>
        <ol className="install-steps">
          {GUIDE_STEPS.map((k) => (
            <li key={k}>{t(k)}</li>
          ))}
        </ol>
        {where !== 'you' && (
          <Button small variant="text" onClick={() => (writeJson(DISMISSED, 1), setShown(false))}>
            {t('install.notNow')}
          </Button>
        )}
      </div>
      <span className={`install-guide-arrow ${pad ? 'up' : 'down'}`} aria-hidden="true">
        <Icon svg={pad ? arrowUpRight : arrowDown} size={32} />
      </span>
    </aside>
  );
}

/**
 * The sign-in page's "Install desyr" and the install dialog; "?install" on any address opens it, on an
 * iPhone or iPad in Safari the guide instead: signed out the sign-in page's, signed in You's (an iPad:
 * Settings → About); installed, nothing (32, By address).
 */
export function InstallOffer({ button }: { button?: boolean }) {
  const state = useInstall();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (button || !byAddress) return;
    if (['installed', 'unsupported'].includes(installState())) return void (byAddress = false);
    if (!iosSafari()) return void ((byAddress = false), setOpen(true));
    if (!readSession()) return; // the sign-in page's guide shows
    byAddress = false;
    aboutAsked = !/iPhone/.test(navigator.userAgent);
    navigate('/you/', { replace: true });
  }, [button]);
  const offered = !['installed', 'unsupported'].includes(state);
  let body: ReactNode;
  if (state === 'installed') body = <p className="dialog-text">{t('install.done')}</p>;
  else if (state === 'available') body = <p className="dialog-text">{t('install.availableLine')}</p>;
  else if (state === 'ios') body = <Steps keys={IOS_STEPS} />;
  else if (state === 'safari') body = <Steps keys={['install.macStep1', 'install.macStep2', 'install.macStep3']} />;
  else body = <p className="dialog-text">{t(LINES[state])}</p>;
  return (
    <>
      {button && iosSafari() && <InstallGuide where="sign-in" />}
      {button && offered && !iosSafari() && (
        <Button className="install-button" data-desyr-install-offer={state} onClick={() => setOpen(true)}>
          <Icon svg={download} size={18} /> {t('install.title')}
        </Button>
      )}
      {open && (
        <Dialog title={t('install.title')} onClose={() => setOpen(false)}>
          <div data-desyr-install-offer={state}>
            {body}
            <TitleBarTip state={state} />
          </div>
          <DialogButtons>
            <Button variant="text" onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
            {state === 'available' && (
              <Button variant="filled" onClick={() => void install()}>
                {t('install.install')}
              </Button>
            )}
          </DialogButtons>
        </Dialog>
      )}
    </>
  );
}

/** The installed app's title bar on a computer (window-controls overlay): a strip across the full width in
 * the rail colour, which drags the window, the logo and the name in the middle of its free part (01, 32, v1.58.62). */
export const TitleBar = () => (
  <div id="desyr-titlebar" aria-hidden="true">
    <span className="titlebar-name">
      <img src={logo} alt="" width={20} height={20} />
      {PRODUCT}
    </span>
  </div>
);

